/**
 * Reusable Conference Room Select Component
 * Uses react-select-async-paginate for efficient loading and pagination
 * Supports location-based filtering and search
 * Styled to match PrimeReact Dropdown components
 */

import React, { useMemo } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadConferenceRoomOptions } from 'hooks/queries/useConferenceRoomsPaginated';
import '../shared/AsyncSelect.scss';

/**
 * ConferenceRoomSelect Component
 *
 * @param {Object} props - Component props
 * @param {string|Object|null} props.value - Selected value (can be ID string, full option object, or null)
 * @param {Function} props.onChange - Change handler (value, actionMeta) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.isClearable - Show clear button
 * @param {boolean} props.isDisabled - Disable the select
 * @param {boolean} props.isSearchable - Enable search (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.officeLocationId - Filter by office location ID
 * @param {number} props.pageSize - Items per page (default: 20)
 * @param {Object} props.styles - Custom react-select styles
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input ID attribute
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @returns {JSX.Element}
 */
function ConferenceRoomSelect({
  value = null,
  onChange,
  placeholder = 'Select conference room',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  className = '',
  officeLocationId = '',
  pageSize = 20,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  // Memoize the load options function with config
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) =>
      loadConferenceRoomOptions(search, loadedOptions, additional, {
        officeLocationId,
        pageSize,
      }),
    [officeLocationId, pageSize]
  );

  // Debounce time for search input (in milliseconds)
  const debounceTimeout = 300;

  // Additional props for AsyncPaginate (client-side pagination uses page number)
  const additional = {
    page: 0,
  };

  // Merge custom styles with menu styling to prevent ResizeObserver issues
  const mergedStyles = useMemo(
    () => ({
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      ...styles,
    }),
    [styles]
  );

  return (
    <AsyncPaginate
      key={officeLocationId} // Force refresh when location changes
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      className={className}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      additional={additional}
      debounceTimeout={debounceTimeout}
      cacheUniqs={[officeLocationId]} // Re-fetch when location changes
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading rooms...'}
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? `No conference rooms found for "${inputValue}"`
          : officeLocationId
          ? 'No conference rooms available for this location'
          : 'No conference rooms available'
      }
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

export default ConferenceRoomSelect;
